import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useState } from "react";
import { 
  Rocket, Menu, X, User, LogOut, Heart, Shield, ChevronDown, ShoppingBag, ShoppingCart, Sparkles, Sun, Moon, Clock, Lightbulb, Scale, MessageSquare, Search, Zap, Users, Trophy
} from "lucide-react";
import FanVerseLogo from "@/components/FanVerseLogo";
import { useTheme } from "@/components/ThemeProvider";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-header">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center hover:opacity-90 transition-opacity"
            data-testid="nav-logo"
          >
            <FanVerseLogo size={36} light />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm text-slate-300 hover:text-white transition-colors" data-testid="nav-explore">
                {t("nav.explore")} <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#0B0F19] border-white/10">
                <DropdownMenuItem asChild>
                  <Link to="/character-chat" className="cursor-pointer flex items-center gap-2" data-testid="nav-character-chat">
                    <MessageSquare className="w-3.5 h-3.5 text-purple-400" /> {t("nav.characterChat")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/compare" className="cursor-pointer flex items-center gap-2" data-testid="nav-compare">
                    <Scale className="w-3.5 h-3.5 text-emerald-400" /> {t("nav.compare")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/quiz" className="cursor-pointer flex items-center gap-2" data-testid="nav-quiz">
                    <Sparkles className="w-3.5 h-3.5 text-pink-400" /> {t("nav.characterMatchmaker")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/search" className="cursor-pointer flex items-center gap-2" data-testid="nav-search">
                    <Search className="w-3.5 h-3.5 text-slate-400" /> {t("nav.advancedSearch")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/leaderboard" className="cursor-pointer flex items-center gap-2" data-testid="nav-leaderboard">
                    <Trophy className="w-3.5 h-3.5 text-amber-400" /> Leaderboard
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm text-slate-300 hover:text-white transition-colors" data-testid="nav-universes-dropdown">
                {t("nav.universes")} <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#0B0F19] border-white/10">
                <DropdownMenuItem asChild>
                  <Link to="/?category=sci-fi" className="cursor-pointer" data-testid="nav-scifi">Sci-Fi</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/?category=fantasy" className="cursor-pointer" data-testid="nav-fantasy">Fantasy</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/?category=superhero" className="cursor-pointer" data-testid="nav-superhero">Superhero</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/?category=horror" className="cursor-pointer" data-testid="nav-horror">Horror</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Link 
              to="/store" 
              className="flex items-center gap-1 text-sm text-slate-300 hover:text-white transition-colors"
              data-testid="nav-store"
            >
              <ShoppingBag className="w-4 h-4" />
              {t("nav.store")}
            </Link>
            <Link 
              to="/lore-explorer" 
              className="flex items-center gap-1 text-sm text-amber-400 hover:text-amber-300 transition-colors"
              data-testid="nav-lore-explorer"
            >
              <Sparkles className="w-4 h-4" />
              {t("nav.loreExplorer")}
            </Link>
            <Link 
              to="/theories" 
              className="flex items-center gap-1 text-sm text-slate-300 hover:text-white transition-colors"
              data-testid="nav-theories"
            >
              <Lightbulb className="w-4 h-4" />
              {t("nav.theories")}
            </Link>
            <Link 
              to="/community" 
              className="flex items-center gap-1 text-sm text-slate-300 hover:text-white transition-colors"
              data-testid="nav-community"
            >
              <Users className="w-4 h-4" />
              {t("nav.community")}
            </Link>
            <button
              onClick={toggleTheme}
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-amber-500 transition-all"
              data-testid="theme-toggle"
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <LanguageSwitcher />
          </div>

          {/* User Actions */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <Link 
                  to="/cart" 
                  className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
                  data-testid="nav-cart"
                >
                  <ShoppingCart className="w-4 h-4" />
                </Link>
                <Link 
                  to="/favorites" 
                  className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
                  data-testid="nav-favorites"
                >
                  <Heart className="w-4 h-4" />
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors" data-testid="nav-user-menu">
                    <User className="w-4 h-4" />
                    {user.name}
                    <ChevronDown className="w-4 h-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#0B0F19] border-white/10">
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="cursor-pointer flex items-center gap-2" data-testid="nav-profile">
                        <User className="w-4 h-4" />
                        {t("nav.myProfile")}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/orders" className="cursor-pointer" data-testid="nav-orders">{t("nav.myOrders")}</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/wishlist" className="cursor-pointer" data-testid="nav-wishlist">{t("nav.wishlist")}</Link>
                    </DropdownMenuItem>
                    {user.role === "admin" && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="cursor-pointer flex items-center gap-2" data-testid="nav-admin">
                            <Shield className="w-4 h-4" />
                            {t("nav.adminPanel")}
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer flex items-center gap-2" data-testid="nav-logout">
                      <LogOut className="w-4 h-4" />
                      {t("nav.logout")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className="text-sm text-slate-300 hover:text-white transition-colors"
                  data-testid="nav-login"
                >
                  {t("nav.signIn")}
                </Link>
                <Link 
                  to="/register" 
                  className="btn-primary px-4 py-2 text-sm"
                  data-testid="nav-register"
                >
                  {t("nav.joinNow")}
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="nav-mobile-menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10">
            <div className="flex flex-col gap-4">
              <Link to="/character-chat" className="text-slate-300 hover:text-white flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <MessageSquare className="w-4 h-4" /> Character Chat
              </Link>
              <Link to="/compare" className="text-slate-300 hover:text-white flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <Scale className="w-4 h-4" /> Compare
              </Link>
              <Link to="/quiz" className="text-slate-300 hover:text-white flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <Sparkles className="w-4 h-4 text-pink-400" /> Character Matchmaker
              </Link>
              <Link to="/search" className="text-slate-300 hover:text-white flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <Search className="w-4 h-4" /> Search
              </Link>
              <Link to="/leaderboard" className="text-slate-300 hover:text-white flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <Trophy className="w-4 h-4 text-amber-400" /> Leaderboard
              </Link>
              <Link to="/store" className="text-slate-300 hover:text-white flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <ShoppingBag className="w-4 h-4" /> Store
              </Link>
              <Link to="/lore-explorer" className="text-amber-400 hover:text-amber-300 flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <Sparkles className="w-4 h-4" /> Lore Explorer
              </Link>
              <Link to="/theories" className="text-slate-300 hover:text-white flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <Lightbulb className="w-4 h-4" /> Theories
              </Link>
              <Link to="/community" className="text-slate-300 hover:text-white flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <Users className="w-4 h-4" /> Community
              </Link>
              <button onClick={() => { toggleTheme(); setMobileMenuOpen(false); }} className="text-slate-300 hover:text-white flex items-center gap-2">
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                {theme === 'dark' ? "Light Mode" : "Dark Mode"}
              </button>
              <Link to="/?category=sci-fi" className="text-slate-300 hover:text-white" onClick={() => setMobileMenuOpen(false)}>Sci-Fi</Link>
              <Link to="/?category=fantasy" className="text-slate-300 hover:text-white" onClick={() => setMobileMenuOpen(false)}>Fantasy</Link>
              <Link to="/?category=superhero" className="text-slate-300 hover:text-white" onClick={() => setMobileMenuOpen(false)}>Superhero</Link>
              <Link to="/?category=horror" className="text-slate-300 hover:text-white" onClick={() => setMobileMenuOpen(false)}>Horror</Link>
              {user ? (
                <>
                  <Link to="/cart" className="text-slate-300 hover:text-white flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                    <ShoppingCart className="w-4 h-4" /> Cart
                  </Link>
                  <Link to="/favorites" className="text-slate-300 hover:text-white" onClick={() => setMobileMenuOpen(false)}>Favorites</Link>
                  <Link to="/wishlist" className="text-slate-300 hover:text-white" onClick={() => setMobileMenuOpen(false)}>Wishlist</Link>
                  <Link to="/orders" className="text-slate-300 hover:text-white" onClick={() => setMobileMenuOpen(false)}>My Orders</Link>
                  {user.role === "admin" && (
                    <Link to="/admin" className="text-slate-300 hover:text-white" onClick={() => setMobileMenuOpen(false)}>Admin Panel</Link>
                  )}
                  <button onClick={handleLogout} className="text-left text-slate-300 hover:text-white">Logout</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-slate-300 hover:text-white" onClick={() => setMobileMenuOpen(false)}>Sign In</Link>
                  <Link to="/register" className="btn-primary px-4 py-2 text-sm text-center" onClick={() => setMobileMenuOpen(false)}>Join Now</Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
